// Custom hook for file upload logic
import { useState, useRef, useCallback, DragEvent, ChangeEvent } from 'react'
// import { apiService } from '../services/api' // No longer needed for local analysis
import { validateFile } from '../utils'
import { FileAnalysis } from '../types'
import { UI_MESSAGES, FILE_UPLOAD } from '../constants'
import Papa from 'papaparse'
// Dynamic import for XLSX - only load when Excel file is encountered

interface UseFileUploadReturn {
    // State
    dragActive: boolean
    selectedFile: File | null
    uploading: boolean
    analyzing: boolean
    fileAnalysis: FileAnalysis | null
    showColumnTable: boolean
    fileInputRef: React.RefObject<HTMLInputElement>

    // Actions
    handleDrag: (e: DragEvent<HTMLDivElement>) => void
    handleDrop: (e: DragEvent<HTMLDivElement>) => void
    handleFileSelect: (e: ChangeEvent<HTMLInputElement>) => void
    openFileDialog: () => void
    analyzeFileAutomatically: (file: File) => Promise<void>
    reset: () => void
    updateColumn: (columnName: string, field: keyof import('../types').FileColumn, value: string) => void
}

export const useFileUpload = (): UseFileUploadReturn => {
    const [dragActive, setDragActive] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)
    const [analyzing, setAnalyzing] = useState(false)
    const [fileAnalysis, setFileAnalysis] = useState<FileAnalysis | null>(null)
    const [showColumnTable, setShowColumnTable] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleDrag = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true)
        } else if (e.type === 'dragleave') {
            setDragActive(false)
        }
    }, [])

    const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0]
            const validation = validateFile(file)

            if (validation.isValid) {
                setSelectedFile(file)
                // File is ready for user to choose metadata option
            } else {
                alert(validation.error)
            }
        }
    }, [])

    const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            const validation = validateFile(file)

            if (validation.isValid) {
                setSelectedFile(file)
                // File is ready for user to choose metadata option
            } else {
                alert(validation.error)
            }
        }
    }, [])

    const openFileDialog = useCallback(() => {
        fileInputRef.current?.click()
    }, [])

    const inferType = (values: any[]): string => {
        // Simple heuristic: test boolean, integer, float, timestamp, date, else string
        const sample = values.filter(v => v !== null && v !== undefined && v !== '')
        if (sample.length === 0) return 'String'
        const boolMatch = sample.every(v => /^(true|false)$/i.test(String(v).trim()))
        if (boolMatch) return 'Boolean'
        const intMatch = sample.every(v => /^-?\d+$/.test(String(v).trim()))
        if (intMatch) return 'Integer'
        const floatMatch = sample.every(v => /^-?\d+(\. \d+)?$/.test(String(v).trim()))
        if (floatMatch) return 'Float'
        // Timestamp: contains date + time component (e.g. "2024-01-15 10:30:00" or ISO with T)
        const tsMatch = sample.every(v => {
            const s = String(v).trim()
            return !isNaN(Date.parse(s)) && /[T :]/.test(s)
        })
        if (tsMatch) return 'Timestamp'
        const dateMatch = sample.every(v => !isNaN(Date.parse(String(v))))
        if (dateMatch) return 'Date'
        return 'String'
    }

    const analyzeFileAutomatically = useCallback(async (file: File) => {
        // Hide previous table view; start parsing
        setShowColumnTable(false)
        setAnalyzing(true)
        let completedAnalysis: FileAnalysis | null = null
        try {
            const nameParts = file.name.split('.')
            const extension = nameParts.length > 1 ? nameParts.pop()?.toLowerCase() : ''
            let rows: Record<string, any>[] = []

            if (extension === 'csv') {
                const textProvider = (file as any).text ? () => (file as any).text() : () => new Promise<string>((resolve, reject) => {
                    try {
                        const reader = new FileReader()
                        reader.onload = () => resolve(reader.result as string)
                        reader.onerror = () => reject(reader.error)
                        reader.readAsText(file)
                    } catch (err) {
                        reject(err)
                    }
                })
                const text = await textProvider()
                const parsed = Papa.parse<Record<string, any>>(text, { header: true, dynamicTyping: false, skipEmptyLines: true })
                rows = (parsed.data as Record<string, any>[]).filter((r: Record<string, any>) => Object.keys(r).length > 0)
            } else if (extension === 'xlsx' || extension === 'xls') {
                // Dynamic import for XLSX to reduce initial bundle size
                const XLSX = await import('xlsx')
                const arrayBuffer = await file.arrayBuffer()
                const workbook = XLSX.read(arrayBuffer, { type: 'array' })
                const sheetName = workbook.SheetNames[0]
                const sheet = workbook.Sheets[sheetName]
                const json = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' })
                rows = json
            } else {
                // Fallback: sometimes test / polyfilled File may not retain name extension but MIME type is present
                if (file.type === 'text/csv' || file.type === '') {
                    const textProvider = (file as any).text ? () => (file as any).text() : () => new Promise<string>((resolve, reject) => {
                        try {
                            const reader = new FileReader()
                            reader.onload = () => resolve(reader.result as string)
                            reader.onerror = () => reject(reader.error)
                            reader.readAsText(file)
                        } catch (err) {
                            reject(err)
                        }
                    })
                    const text = await textProvider()
                    const firstLine = text.split(/\r?\n/)[0] || ''
                    try {
                        const parsed = Papa.parse<Record<string, any>>(text, { header: true, dynamicTyping: false, skipEmptyLines: true })
                        const parsedRows = (parsed.data as Record<string, any>[]).filter((r: Record<string, any>) => Object.keys(r).length > 0)
                        const headerLooksCsv = /[,]/.test(firstLine) && parsed.meta && Array.isArray((parsed as any).meta.fields)
                        if (headerLooksCsv && parsedRows.length >= 0) {
                            rows = parsedRows
                        } else {
                            throw new Error('Unsupported file format')
                        }
                    } catch (e) {
                        throw new Error('Unsupported file format')
                    }
                } else {
                    throw new Error('Unsupported file format')
                }
            }

            // Build column metadata
            const columnsSet = new Set<string>()
            rows.slice(0, 50).forEach(r => Object.keys(r).forEach(k => columnsSet.add(k)))
            const columns = Array.from(columnsSet)

            const columnMeta = columns.map(name => {
                const sampleValues = rows.slice(0, FILE_UPLOAD.PREVIEW_ROWS).map(r => r[name]).filter(v => v !== undefined && v !== null).map(v => String(v))
                const inferred = inferType(sampleValues)
                return {
                    name,
                    detectedType: inferred as any,
                    selectedType: inferred as any,
                    description: '',
                    sampleValues: sampleValues.slice(0, 8) // Increase to 8 sample values to match API
                }
            })

            const previewData = rows.slice(0, FILE_UPLOAD.PREVIEW_ROWS)
            const analysis: FileAnalysis = {
                fileName: file.name,
                fileSize: file.size,
                columns: columnMeta,
                recordCount: rows.length,
                totalRows: rows.length,
                previewData,
                source: 'local'
            }

            // Artificial delay removed (previously enforced 3s minimum). We now rely purely on real parsing time.

            setFileAnalysis(analysis)
            completedAnalysis = analysis
        } catch (error) {
            console.error('Failed to analyze file locally:', error)
            if (typeof window !== 'undefined' && typeof window.alert === 'function') {
                try {
                    alert(UI_MESSAGES.UPLOAD.ANALYSIS_FAILED)
                } catch {
                    // ignore alert issues in non-browser / jsdom
                }
            }
        } finally {
            setAnalyzing(false)
            // Only reveal table after analyzing flag cleared for UX consistency
            if (completedAnalysis) {
                setShowColumnTable(true)
            }
        }
    }, [])

    const updateColumn = useCallback((columnName: string, field: keyof import('../types').FileColumn, value: string) => {
        if (!fileAnalysis || !fileAnalysis.columns) return

        setFileAnalysis({
            ...fileAnalysis,
            columns: fileAnalysis.columns.map(col =>
                col.name === columnName ? { ...col, [field]: value } : col
            )
        })
    }, [fileAnalysis])

    const reset = useCallback(() => {
        setSelectedFile(null)
        setFileAnalysis(null)
        setShowColumnTable(false)
        setAnalyzing(false)
        setUploading(false)
        setDragActive(false)
        // Clear the file input value to ensure clean state
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [])

    return {
        // State
        dragActive,
        selectedFile,
        uploading,
        analyzing,
        fileAnalysis,
        showColumnTable,
        fileInputRef,

        // Actions
        handleDrag,
        handleDrop,
        handleFileSelect,
        openFileDialog,
        analyzeFileAutomatically,
        reset,
        updateColumn
    }
}